/**
 * GatedLayout — the persistent shell for every signed-in (gated) page.
 *
 * Story: epic-uat-portal-001-14.
 *
 * Wraps the `ProtectedRoute` children (`/app`, `/report`, `/board`) in one
 * shared header so a tester can move between the guide, the bug-report form, and
 * the status board from ANYWHERE — not just the signed-in home. Before this, the
 * gated pages were orphaned: `/report` (-10) and `/board` (-11) were fully built
 * and routed but reachable only by typing the URL, because no authed nav was ever
 * wired and the only `/guide` links lived on the public landing.
 *
 * Mounted as a layout route (`<ProtectedRoute><GatedLayout/></ProtectedRoute>`
 * wrapping the three gated routes), so the header is defined ONCE and the matched
 * child renders through `<Outlet/>` — never copy-pasted into each page.
 *
 * `/guide` is public but belongs in the authed nav too: a signed-in tester still
 * wants the "How to test" guide one click away.
 *
 * Nav entries are React Router `<Link>`s (real anchors — middle-click / copy-link
 * / screen-reader semantics). Sign out is an action, so it stays a `<button>`
 * calling `logout`. Copy mirrors the plain/clear tester voice of LandingPage.
 */

import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTesterAuth } from '@/auth/testerAuthContext';

// NavLink appends an `active` class and sets aria-current="page" on the matching
// route, so a tester can see which gated page they're on.
const NAV_LINK_CLASS = 'underline';

export function GatedLayout() {
  const { logout } = useTesterAuth();

  return (
    <div data-testid="gated-layout" className="min-h-screen">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 px-6 py-3 text-sm">
        <Link to="/app" data-testid="nav-home" className="font-semibold">
          Hello World DAO — Tester Portal
        </Link>
        {/* aria-label distinguishes this authed shell nav from the public
            landing's nav (two <nav> landmarks live in the app). */}
        <nav aria-label="Tester navigation" className="flex flex-wrap items-center gap-4">
          <NavLink to="/guide" data-testid="nav-guide" className={NAV_LINK_CLASS}>
            How to test
          </NavLink>
          <NavLink to="/report" data-testid="nav-report" className={NAV_LINK_CLASS}>
            Report a bug
          </NavLink>
          <NavLink to="/board" data-testid="nav-board" className={NAV_LINK_CLASS}>
            Status board
          </NavLink>
          <button
            type="button"
            onClick={logout}
            data-testid="nav-logout"
            className="rounded border border-gray-300 px-3 py-1.5 font-medium"
          >
            Sign out
          </button>
        </nav>
      </header>

      <Outlet />
    </div>
  );
}
